/**
 * Public Worlds Page (pre-login front door)
 *
 * Thin wrapper that renders the shared {@link WorldsPage} OUTSIDE the AuthGuard
 * (see main.tsx's `#worlds` branch). It supplies the pre-login behaviours that
 * differ from the in-app entry:
 *
 * - onSignupHandoff: a logged-OUT visitor's chosen world is stashed in
 *   sessionStorage (WORLD_INTENT_KEY) and the hash is set to `#signup` so the
 *   app falls through to AuthGuard's signup view. App.tsx consumes the intent
 *   after auth and opens CampaignWizard prefilled. Mirrors authStore's pending
 *   beta-key OAuth pattern.
 * - onEnterCampaign: a logged-IN visitor who lands here (e.g. external link) can
 *   open the wizard inline; on create we clear the hash so main.tsx renders the
 *   app and stash the new campaign id for App to enter.
 * - onBack: leave the front door for the app (login or lobby).
 *
 * No backend, no API, no DB — the gallery reads static PLAYSETS.
 */

import { WorldsPage, WORLD_INTENT_KEY, WORLD_INTENT_CREATE } from './WorldsPage';
import { PlaysetId } from '@machina/core';

/**
 * sessionStorage key for a campaign a logged-in visitor created from the public
 * front door. App.tsx enters it once the app mounts. Distinct from the signup
 * intent (which only carries a PlaysetId).
 */
export const ENTER_CAMPAIGN_KEY = 'machina:enterCampaignId';

export function PublicWorldsPage() {
  // Stash the chosen world and route to signup. Setting the hash to `#signup`
  // takes us out of main.tsx's `#worlds` branch, so we fall through to
  // <AuthGuard>, which reads the hash to open its signup view.
  const handleSignupHandoff = (playsetId: PlaysetId | null) => {
    sessionStorage.setItem(WORLD_INTENT_KEY, playsetId ?? WORLD_INTENT_CREATE);
    window.location.hash = '#signup';
  };

  // Logged-in visitor created a campaign from the public page: hand off to the
  // app (clear the hash) and stash the id for App to enter on mount.
  const handleEnterCampaign = (campaignId: string) => {
    sessionStorage.setItem(ENTER_CAMPAIGN_KEY, campaignId);
    window.location.hash = '';
  };

  // Leave the front door for the app shell (AuthGuard → login, or lobby).
  const handleBack = () => {
    window.location.hash = '';
  };

  return (
    <WorldsPage
      onSignupHandoff={handleSignupHandoff}
      onEnterCampaign={handleEnterCampaign}
      onBack={handleBack}
    />
  );
}
